/*---------------React & Libraries-----------------*/
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";

/*---------------Material UI Components-----------*/
import Grid from "@mui/material/Grid";
import {
  Box,
  Typography,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Stack,
  Paper,
} from "@mui/material";
// import { Download } from "@mui/icons-material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Inventory2Icon from "@mui/icons-material/Inventory2";

/*---------------Stores-----------*/
import { useStore } from "../../app/stores/store";
/*---------------Types-----------*/
//import { type OrderDetail } from "../../app/models/OrderDetail";
import { observer } from "mobx-react-lite";

/*---------------Utils-----------*/
import {
  renderOrderStatus,
  renderOrderDeliveryType,
} from "../../app/shared/utils/miscellaneous";

// TODO - Common method
const DATE_FORMAT = "dd/MM/yyyy hh:mm a";
const formatOrderDate = (date: string) => format(new Date(date), DATE_FORMAT);

const OrderDetail: React.FC = () => {
  const { orderStore, toasterStore, commonStore } = useStore();
  const { order, fetchOrderDetail, reorder } = orderStore;
  const navigate = useNavigate();

  const { orderId } = useParams();

  // Determine if delivery type is standard (1) or quick (2)
  const isStandardDelivery = order?.order_delivery_type === 1;
  const isScheduledDelivery = order?.order_delivery_type === 3;

  useEffect(() => {
    const fetchData = async () => {
      await fetchOrderDetail(Number(orderId));
    };
    fetchData();
    // @ts-ignore
  }, [fetchOrderDetail, orderId]);

  const handleReorder = async () => {
    try {
      const payload = {
        orderId: Number(order?.id),
        warehouseId: Number(order?.ware_house_id),
      };

      const headers = {
        customerTypeId: Number(order?.customer_outlet?.customerTypeId),
        customerOrgId: Number(order?.customer_outlet?.customerOrgId),
        outletId: Number(order?.customer_outlet?.id),
      };
      const response = await reorder(payload, headers);
      // Scheduled (3) is standard delivery with a slot, so map to 1 for cart API
      const cartDeliveryType = order?.order_delivery_type === 3 ? 1 : order?.order_delivery_type;
      const reorderRoute = `/cartDetail?outletId=${order?.customer_outlet?.id}&warehouseId=${order?.ware_house_id}&customerTypeId=${order?.customer_outlet?.customerTypeId}&deliveryType=${cartDeliveryType}`;
      if (response?.status_code === 1) {
        navigate(reorderRoute);
      }
    } catch (error) {
      toasterStore.error("Something went wrong");
    }
  };

  if (!order) {
    return <Typography>Loading...</Typography>;
  }

  // Get slot time directly from order response
  const slotTime = order?.slot ? `${order.slot.from_time} - ${order.slot.to_time}` : null;

  return (
    <Box p={{ sx: 1, md: 3 }}>
      <Typography variant="h6" fontWeight="bold">
        Order for {order?.customer_outlet.name}
      </Typography>

      <Grid container spacing={3} mt={1}>
        {/* Left Panel */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Delivery Address */}
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <LocationOnIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Delivery Address
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {order?.customer_outlet.shippingAddressLine1},
              {order?.customer_outlet.shippingAddressLine2},
              {order?.customer_outlet?.shippingCity?.name},{" "}
              {order?.customer_outlet?.shippingPincode},
              {order?.customer_outlet?.shippingState?.name}
            </Typography>
          </Paper>

          {/* Ordered Items */}
          <Paper sx={{ p: 2, mt: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <Inventory2Icon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                Ordered Items
              </Typography>
            </Stack>
            <List disablePadding>
              {order?.order_items.map((item) => {
                const isEdited =
                  item?.is_edited && item?.quantity !== item?.adjust_quantity;
                const hasReturns = Number(item?.total_returned_items) > 0;

                return (
                  <ListItem
                    key={item?.id}
                    disableGutters
                    sx={{ alignItems: "flex-start", py: 1.5 }}
                  >
                    <Avatar
                      src={item?.product_images}
                      sx={{ mr: 2, width: 64, height: 64 }}
                      variant="rounded"
                    />
                    <ListItemText
                      primary={
                        <Typography variant="body1" fontWeight="medium">
                          {item?.name}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {/* Quantity Labels */}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                          >
                            {isEdited ? (
                              <>
                                <Box
                                  component="span"
                                  sx={{ display: "inline-block", mr: 2 }}
                                >
                                  <strong>Ordered Qty:</strong> {item?.quantity}
                                </Box>
                                <Box
                                  component="span"
                                  sx={{ display: "inline-block", mr: 2 }}
                                >
                                  <strong>Invoiced Qty:</strong>{" "}
                                  {item?.adjust_quantity}
                                </Box>
                                {hasReturns && (
                                  <Box
                                    component="span"
                                    sx={{ display: "inline-block" }}
                                  >
                                    <strong>Remaining Qty:</strong>{" "}
                                    {item?.total_remaining_items}
                                  </Box>
                                )}
                              </>
                            ) : hasReturns ? (
                              <>
                                <Box
                                  component="span"
                                  sx={{ display: "inline-block", mr: 2 }}
                                >
                                  <strong>Ordered Qty:</strong> {item?.quantity}
                                </Box>
                                <Box
                                  component="span"
                                  sx={{ display: "inline-block" }}
                                >
                                  <strong>Remaining Qty:</strong>{" "}
                                  {item?.total_remaining_items}
                                </Box>
                              </>
                            ) : (
                              <Box component="span">
                                <strong>Ordered Qty:</strong> {item?.quantity}
                              </Box>
                            )}
                          </Typography>

                          {/* Price Display */}
                          <Typography
                            variant="body1"
                            fontWeight="medium"
                            color="primary"
                          >
                            {isEdited ? (
                              <>
                                {hasReturns ? (
                                  <>
                                    ₹
                                    {(
                                      Number(item?.total_remaining_items) *
                                      item?.item_price
                                    ).toFixed(2)}{" "}
                                    <span
                                      style={{
                                        textDecoration: "line-through",
                                        color: "gray",
                                        marginLeft: 4,
                                      }}
                                    >
                                      ₹
                                      {(
                                        Number(item?.adjust_quantity) *
                                        item?.item_price
                                      ).toFixed(2)}
                                    </span>{" "}
                                    <span
                                      style={{
                                        textDecoration: "line-through",
                                        color: "gray",
                                        marginLeft: 4,
                                      }}
                                    >
                                      ₹
                                      {(
                                        Number(item?.quantity) *
                                        item?.item_price
                                      ).toFixed(2)}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    ₹
                                    {(
                                      Number(item?.adjust_quantity) *
                                      item?.item_price
                                    ).toFixed(2)}{" "}
                                    <span
                                      style={{
                                        textDecoration: "line-through",
                                        color: "gray",
                                        marginLeft: 4,
                                      }}
                                    >
                                      ₹
                                      {(
                                        Number(item?.quantity) *
                                        item?.item_price
                                      ).toFixed(2)}
                                    </span>
                                  </>
                                )}
                              </>
                            ) : hasReturns ? (
                              <>
                                ₹
                                {(
                                  Number(item?.total_remaining_items) *
                                  item?.item_price
                                ).toFixed(2)}{" "}
                                <span
                                  style={{
                                    textDecoration: "line-through",
                                    color: "gray",
                                    marginLeft: 4,
                                  }}
                                >
                                  ₹
                                  {(
                                    Number(item?.quantity) * item?.item_price
                                  ).toFixed(2)}
                                </span>
                              </>
                            ) : (
                              <>
                                ₹
                                {(
                                  Number(item?.quantity) * item?.item_price
                                ).toFixed(2)}
                              </>
                            )}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
            {/* Show reorder button only if warehouse_id matches the warehouse from getWarehouseId API */}
            {order?.ware_house_id ===
              commonStore.warehouseId && (
              <Button variant="outlined" sx={{ mt: 2 }} onClick={handleReorder}>
                Reorder
              </Button>
            )}
          </Paper>
        </Grid>

        {/* Right Panel */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={2}>
            {/* Buttons */}
            {/* <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Download />}>
                Invoice
              </Button>
            </Stack> */}

            {/* Order & Delivery Details */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Order & Delivery Details
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                <b>Order Id:</b> {order?.unique_order_id}
              </Typography>
              <Typography variant="body2">
                <b>Order Date:</b> {formatOrderDate(order.createdAt)}
              </Typography>
              <Typography variant="body2" color="error">
                <b>Order Status:</b> {renderOrderStatus(order.order_status)}
              </Typography>
              <Typography variant="body2">
                <b>Delivery Type:</b>{" "}
                {renderOrderDeliveryType(order?.order_delivery_type)}
              </Typography>
              {/* Show Delivery Slot only for Standard Delivery */}
              {(isStandardDelivery || isScheduledDelivery) && (
                <Typography variant="body2">
                  <b>Delivery Slot:</b> {slotTime || "N/A"}
                </Typography>
              )}
            </Paper>

            {/* User & Payment Details */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                User & Payment Details
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                <b>Email:</b> {order.customer_outlet.email}
              </Typography>
              <Typography variant="body2">
                <b>Mobile:</b> {order.customer_outlet.mobile}
              </Typography>
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                <b>Payment Status:</b> {order.payment_status}
              </Typography>
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                <b>Payment Mode:</b> {order?.payment_method}
              </Typography>
            </Paper>

            {/* Bill Details */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Bill Details
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">
                <b>Sub Total: </b>
                {order?.adjustSubTotal &&
                order?.adjustSubTotal !== order?.subTotal ? (
                  <>
                    ₹{order?.adjustSubTotal}{" "}
                    <span
                      style={{
                        textDecoration: "line-through",
                        marginLeft: 4,
                        color: "gray",
                      }}
                    >
                      ₹{order?.subTotal}
                    </span>
                  </>
                ) : (
                  <>₹{order?.subTotal}</>
                )}
              </Typography>

              <Typography variant="body2">
                <b>GST: </b>
                {order?.adjustTax && order?.adjustTax !== order?.tax ? (
                  <>
                    ₹{order?.adjustTax}{" "}
                    <span
                      style={{
                        textDecoration: "line-through",
                        marginLeft: 4,
                        color: "gray",
                      }}
                    >
                      ₹{order?.tax}
                    </span>
                  </>
                ) : (
                  <>₹{order?.tax}</>
                )}
              </Typography>
              <Typography variant="body2">
                <b>Delivery Charges:</b>{" "}
                {order.delivery_cost === 0 ? "FREE" : `₹${order.delivery_cost}`}
              </Typography>
              <Typography variant="body2">
                <b>Small Cart Charges:</b>{" "}
                {order.small_cart_cost === 0
                  ? "FREE"
                  : `₹${order.small_cart_cost}`}
              </Typography>
              {order?.coupon_discount_amount > 0 ? (
                <Typography variant="body2" color="success.main">
                  <b>Coupon Discount:</b> -₹
                  {Number(order.coupon_discount_amount).toFixed(2)}
                </Typography>
              ) : (
                <></>
              )}
              <Typography variant="body1" fontWeight="bold">
                <b>Total Amount:</b> ₹{order.total_collectible_amount}
              </Typography>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default observer(OrderDetail);
